import { clearCurrentEditedBomOption, setCurrentEditedBomOption, updateRecentBomOptionsList } from '../../../actions';
import {
  deselectMultipleSelectionOption,
  selectMultipleSelectionOption,
  toggleSingleSelectionOption,
} from '../../../../bom/actions';
import { dispatchBomColorChangeEvent, reloadUniqueOptions } from '../../../../bom/logics';
import { getCurrentEditedBomOption, getMostRecentlySelectedBomOption } from '../../../product/selectors';
import { getUniqueOptions, getWhetherNeedToDispatchBomColorChangeEvent } from '../../../../bom/selectors';
import { useDispatch, useSelector } from 'react-redux';

import AdditionalSwatchSelector from '../AdditionalSwatchSelector';
import GraphicOptionGroup from '../GraphicOptionGroup';
import PropTypes from 'prop-types';
import QuantityGroup from '../QuantityGroup';
import { QuestionType } from '../../../../bom/constants';
import React from 'react';
import { updateCurrentGalleryItemAsync } from '../../../product/logics';
import { updatePricingIfNeededForYearOptions } from '../../../../main/logics';

const GraphicMultiSelectQuestion = ({ feedback, question, resetFeedback }) => {
  const dispatch = useDispatch();

  const options = useSelector((state) => getUniqueOptions(state, question.code));
  const needToDispatchBomColorChangeEvent = useSelector((state) =>
    getWhetherNeedToDispatchBomColorChangeEvent(state, question.code)
  );
  const currentEditedBomOption = useSelector(getCurrentEditedBomOption);
  const mostRecentlySelectedBomOption = useSelector((state) => getMostRecentlySelectedBomOption(state, question.code));

  const hasMultipleSelectableOptions = options.filter((option) => !option.isSoldOut).length > 1;
  const hasOptionSelected = options.some((option) => option.isSelected);
  const hasAvailableOptions = options.some((option) => !option.isSelected && !option.isSoldOut);

  const handleDispatchBomColorChangeEvent = (isMultiSelect, option) => {
    if (needToDispatchBomColorChangeEvent) {
      dispatchBomColorChangeEvent({ isMultiSelect, option });
    }
  };

  const handleSelectOption = (multiSelectMode, option) => {
    let dispatchedOption;
    let multiSelect = multiSelectMode;

    if (multiSelectMode) {
      if (option.isSelected) {
        dispatch(deselectMultipleSelectionOption(option.code, option.codeValue));
      } else {
        dispatch(selectMultipleSelectionOption(option.code, option.codeValue, 0));
      }
    } else {
      if (option.isSelected) {
        return;
      }

      const hasMultipleOptionsSelected = options.filter((option) => option.isSelected).length > 1;
      const hasQuantitiesEntered = options.some((option) => option.isSelected && option.quantity > 0);
      if (hasMultipleOptionsSelected || hasQuantitiesEntered) {
        if (!!mostRecentlySelectedBomOption) {
          const carriedOverQuantity = options.find(
            (option) => option.codeValue === mostRecentlySelectedBomOption.codeValue
          ).quantity;

          dispatchedOption = { ...mostRecentlySelectedBomOption, isSelected: false };
          multiSelect = true;
          dispatch(
            deselectMultipleSelectionOption(mostRecentlySelectedBomOption.code, mostRecentlySelectedBomOption.codeValue)
          );
          dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
          handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);

          dispatch(selectMultipleSelectionOption(option.code, option.codeValue, carriedOverQuantity));
        }
      } else {
        dispatch(toggleSingleSelectionOption(option.code, option.codeValue));
      }
    }

    if (question.questionType !== QuestionType.GENERAL) {
      dispatch(reloadUniqueOptions(question));
      dispatch(updatePricingIfNeededForYearOptions());
    }

    resetFeedback(option.code);
    resetFeedback('quantity');

    dispatchedOption = { ...option, isSelected: !option.isSelected };
    dispatch(dispatchedOption.isSelected ? setCurrentEditedBomOption(dispatchedOption) : clearCurrentEditedBomOption());
    dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
    dispatch(updateCurrentGalleryItemAsync(dispatchedOption));
    handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);
  };

  const handleRemoveOption = (option) => {
    dispatch(deselectMultipleSelectionOption(option.code, option.codeValue));
    if (question.questionType !== QuestionType.GENERAL) {
      dispatch(reloadUniqueOptions(question));
      dispatch(updatePricingIfNeededForYearOptions());
    }
    resetFeedback(option.code);
    resetFeedback('quantity');

    const multiSelect = true;
    const dispatchedOption = { ...option, isSelected: false };
    if (!!currentEditedBomOption && currentEditedBomOption.codeValue === dispatchedOption.codeValue) {
      dispatch(clearCurrentEditedBomOption());
    }
    dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
    dispatch(updateCurrentGalleryItemAsync(dispatchedOption));
    handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);
  };

  const handleSaveQuantity = (option, qty) => {
    if (option.quantity !== qty) {
      dispatch(selectMultipleSelectionOption(option.code, option.codeValue, qty));
    }
  };

  return (
    <>
      <GraphicOptionGroup
        feedback={feedback}
        options={options}
        question={question}
        onSelectOption={hasMultipleSelectableOptions ? handleSelectOption : undefined}
      />
      {hasOptionSelected && (
        <>
          <hr />
          <QuantityGroup
            feedback={feedback}
            options={options}
            question={question}
            resetFeedback={resetFeedback}
            onRemoveOption={hasMultipleSelectableOptions ? handleRemoveOption : undefined}
            onSaveQuantity={handleSaveQuantity}
          />
          {hasAvailableOptions && <AdditionalSwatchSelector question={question} onSelectOption={handleSelectOption} />}
        </>
      )}
    </>
  );
};

GraphicMultiSelectQuestion.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  question: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  resetFeedback: PropTypes.func.isRequired,
};

export default GraphicMultiSelectQuestion;
